import React, { useState, useEffect, useMemo } from 'react';
import { getApiBase } from '../api';
import { useAuth } from '../components/AuthContext';

export default function ShipperProfile() {
  const apiBase = useMemo(() => getApiBase(), []);
  const { shipperEmail } = useAuth();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  // Notification prefs
  const [notifLevel, setNotifLevel] = useState('ALL_UPDATES');
  const [notifyPickup, setNotifyPickup] = useState(true);
  const [notifyDelivery, setNotifyDelivery] = useState(true);
  const [notifyStatusChange, setNotifyStatusChange] = useState(true);
  const [notifyEta, setNotifyEta] = useState(false);
  const [notifyDocs, setNotifyDocs] = useState(false);
  const [notifyDigest, setNotifyDigest] = useState(false);
  const [notifChannels, setNotifChannels] = useState(['EMAIL']);
  const [smsEnabled, setSmsEnabled] = useState(false);

  useEffect(() => {
    if (!shipperEmail) return;
    setLoading(true);
    fetch(`${apiBase}/shipper/profile`, {
      headers: { 'x-shipper-email': shipperEmail }
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setProfile(data);
          setNotifLevel(data.notificationLevel || 'ALL_UPDATES');
          setNotifyPickup(data.notifyOnPickup !== false);
          setNotifyDelivery(data.notifyOnDelivery !== false);
          setNotifyStatusChange(data.notifyOnStatusChange !== false);
          setNotifyEta(data.notifyOnEtaChange === true);
          setNotifyDocs(data.notifyOnDocuments === true);
          setNotifyDigest(data.notifyDailyDigest === true);
          setNotifChannels(data.notificationChannels || ['EMAIL']);
          setSmsEnabled(data.smsEnabled === true);
        }
      })
      .finally(() => setLoading(false));
  }, [apiBase, shipperEmail]);

  async function handleSave() {
    setSaving(true);
    setSaveMsg('');
    try {
      const res = await fetch(`${apiBase}/shipper/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-shipper-email': shipperEmail },
        body: JSON.stringify({
          notificationLevel: notifLevel,
          notifyOnPickup: notifyPickup,
          notifyOnDelivery: notifyDelivery,
          notifyOnStatusChange: notifyStatusChange,
          notifyOnEtaChange: notifyEta,
          notifyOnDocuments: notifyDocs,
          notifyDailyDigest: notifyDigest,
          notificationChannels: notifChannels,
          smsEnabled,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Save failed');
      setSaveMsg('✅ Preferences saved!');
    } catch (e) {
      setSaveMsg('❌ ' + e.message);
    } finally {
      setSaving(false);
    }
  }

  if (!shipperEmail) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Set a Shipper Email in the dev panel.</div>
  );
  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading profile...</div>;

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #0f172a, #1e3a5f)',
        borderRadius: 14, padding: '24px 28px', marginBottom: 24, color: 'white'
      }}>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 900 }}>⚙️ My Profile & Preferences</h1>
        <p style={{ margin: '6px 0 0', fontSize: 13, opacity: 0.7 }}>
          {profile?.name || 'Shipper'} • {shipperEmail}
        </p>
      </div>

      {/* Company Info (read-only) */}
      {profile && (
        <div style={{ background: 'white', borderRadius: 14, border: '1px solid #e5e7eb', padding: 24, marginBottom: 16 }}>
          <h3 style={{ margin: '0 0 14px', fontSize: 16, fontWeight: 800 }}>🏢 Company Information</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Company</div>
              <div style={{ fontSize: 15, fontWeight: 700, marginTop: 4 }}>{profile.name || '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Email</div>
              <div style={{ fontSize: 15, fontWeight: 700, marginTop: 4 }}>{profile.email || shipperEmail}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Phone</div>
              <div style={{ fontSize: 15, fontWeight: 700, marginTop: 4 }}>{profile.phone || '—'}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280' }}>Location</div>
              <div style={{ fontSize: 15, fontWeight: 700, marginTop: 4 }}>
                {profile.city && profile.state ? `${profile.city}, ${profile.state}` : '—'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SMS Opt-in */}
      <div style={{ background: 'white', borderRadius: 14, border: '1px solid #e5e7eb', padding: 24, marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 14px', fontSize: 16, fontWeight: 800 }}>📱 SMS Notifications</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div onClick={() => setSmsEnabled(!smsEnabled)}
            style={{ width: 52, height: 28, borderRadius: 14, cursor: 'pointer', padding: 2,
                     background: smsEnabled ? '#2563eb' : '#d1d5db', transition: 'all 0.2s', position: 'relative' }}>
            <div style={{
              width: 24, height: 24, borderRadius: '50%', background: 'white',
              position: 'absolute', top: 2,
              left: smsEnabled ? 26 : 2, transition: 'left 0.2s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
            }} />
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>
              {smsEnabled ? '📱 SMS Enabled' : 'SMS Disabled'}
            </div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              Receive text message alerts about your shipments
            </div>
          </div>
        </div>
        {smsEnabled && (
          <div style={{ marginTop: 10, fontSize: 12, color: '#6b7280', padding: '8px 12px', background: '#f9fafb', borderRadius: 8 }}>
            By enabling SMS, you consent to receiving shipment notifications.
            Standard messaging rates apply. <a href="/sms-consent" style={{ color: '#2563eb' }}>SMS Terms</a>
          </div>
        )}
      </div>

      {/* Notification Preferences */}
      <div style={{ background: 'white', borderRadius: 14, border: '1px solid #e5e7eb', padding: 24, marginBottom: 16 }}>
        <h3 style={{ margin: '0 0 14px', fontSize: 16, fontWeight: 800 }}>🔔 Notification Preferences</h3>
        <p style={{ margin: '0 0 14px', fontSize: 13, color: '#6b7280' }}>
          Control how much communication you receive about your shipments.
        </p>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 700, display: 'block', marginBottom: 6 }}>Update Frequency</label>
          <select value={notifLevel} onChange={e => setNotifLevel(e.target.value)}
            style={{ padding: 10, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, width: '100%' }}>
            <option value="ALL_UPDATES">All Updates — Every status change, ETA update, document upload</option>
            <option value="MILESTONES_ONLY">Milestones Only — Pickup confirmation, delivery confirmation</option>
            <option value="DAILY_DIGEST">Daily Digest — One summary email per day</option>
            <option value="MINIMAL">Minimal — Only critical issues requiring attention</option>
          </select>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 700, display: 'block', marginBottom: 8 }}>Notify Me About:</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {[
              { label: '📦 Pickup Confirmation', value: notifyPickup, setter: setNotifyPickup },
              { label: '✅ Delivery Confirmation', value: notifyDelivery, setter: setNotifyDelivery },
              { label: '🔄 Status Changes', value: notifyStatusChange, setter: setNotifyStatusChange },
              { label: '⏱ ETA Changes', value: notifyEta, setter: setNotifyEta },
              { label: '📄 Document Uploads (BOL, POD)', value: notifyDocs, setter: setNotifyDocs },
              { label: '📊 Daily Summary Report', value: notifyDigest, setter: setNotifyDigest },
            ].map(item => (
              <label key={item.label} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                borderRadius: 8, border: '1px solid #e5e7eb', cursor: 'pointer',
                background: item.value ? '#eff6ff' : 'white', transition: 'all 0.15s'
              }}>
                <input type="checkbox" checked={item.value} onChange={() => item.setter(!item.value)}
                  style={{ width: 18, height: 18 }} />
                <span style={{ fontSize: 14, fontWeight: item.value ? 700 : 400 }}>{item.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label style={{ fontSize: 13, fontWeight: 700, display: 'block', marginBottom: 8 }}>Preferred Channels:</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {[
              { code: 'EMAIL', label: '📧 Email' },
              { code: 'SMS', label: '📱 SMS', disabled: !smsEnabled },
              { code: 'PORTAL', label: '🖥 Portal Only' },
            ].map(ch => {
              const active = notifChannels.includes(ch.code);
              return (
                <button key={ch.code} type="button"
                  disabled={ch.disabled}
                  onClick={() => {
                    if (active) setNotifChannels(notifChannels.filter(c => c !== ch.code));
                    else setNotifChannels([...notifChannels, ch.code]);
                  }}
                  style={{
                    padding: '8px 16px', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: ch.disabled ? 'not-allowed' : 'pointer',
                    border: active ? 'none' : '1px solid #e5e7eb',
                    background: active ? '#2563eb' : 'white',
                    color: active ? 'white' : '#374151',
                    opacity: ch.disabled ? 0.4 : 1,
                  }}>
                  {ch.label}
                </button>
              );
            })}
          </div>
          {!smsEnabled && (
            <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>
              Enable SMS above to use text message notifications
            </div>
          )}
        </div>
      </div>

      {/* Save */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <button onClick={handleSave} disabled={saving}
          style={{
            padding: '14px 32px', background: saving ? '#9ca3af' : '#2563eb',
            color: 'white', border: 'none', borderRadius: 12, fontWeight: 800,
            fontSize: 16, cursor: saving ? 'default' : 'pointer'
          }}>
          {saving ? '⏳ Saving...' : '💾 Save Preferences'}
        </button>
        {saveMsg && <span style={{ fontSize: 14, fontWeight: 600 }}>{saveMsg}</span>}
      </div>
    </div>
  );
}
